import React, { useState, useEffect, useContext } from 'react';
import axiosInstance from '../services/axiosInstance';
import Sidebar2 from '../shared/Sidebar2';
import AuthContext from '../services/AuthContext';

// Professional Toast
const toast = {
  _show(msg, type = 'info') {
    const prev = document.getElementById('homemade-toast');
    if (prev) prev.remove();

    const div = document.createElement('div');
    div.id = 'homemade-toast';
    div.style.position = 'fixed';
    div.style.top = '24px';
    div.style.left = '24px';
    div.style.zIndex = 9999;
    div.style.background = type === 'success'
      ? '#16a34a'
      : type === 'error'
      ? '#dc2626'
      : '#2563eb';
    div.style.color = '#fff';
    div.style.padding = '0.7rem 1.2rem';
    div.style.borderRadius = '0.5rem';
    div.style.boxShadow = '0 2px 12px rgba(0,0,0,0.10)';
    div.style.fontWeight = 500;
    div.style.fontSize = '0.98rem';
    div.style.transition = 'opacity 0.3s';
    div.style.opacity = 1;
    div.innerText = msg;

    document.body.appendChild(div);

    setTimeout(() => {
      div.style.opacity = 0;
      setTimeout(() => {
        if (div.parentNode) div.parentNode.removeChild(div);
      }, 400);
    }, 2200);
  },
  success(msg) { this._show(msg, 'success'); },
  error(msg) { this._show(msg, 'error'); },
  info(msg) { this._show(msg, 'info'); }
};

export default function AdminAlert() {
  // States
  const [users, setUsers] = useState([]);
  const [groups, setGroups] = useState([]);
  const [matiers, setMatiers] = useState([]);
  const [messageTemplates, setMessageTemplates] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [selectedMatier, setSelectedMatier] = useState('');
  const [presentStudents, setPresentStudents] = useState(new Set());
  const [lessonTitle, setLessonTitle] = useState('');
  const [messageType, setMessageType] = useState('absence_alert');
  const [customMessage, setCustomMessage] = useState('');
  const [currentView, setCurrentView] = useState('groups');
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [connectionError, setConnectionError] = useState('');
  const [showCreateGroup, setShowCreateGroup] = useState(false);
  const [newGroupName, setNewGroupName] = useState('');
  const [newGroupMatiere, setNewGroupMatiere] = useState('');
  const [selectedStudentsForGroup, setSelectedStudentsForGroup] = useState(new Set());
  const [availableStudents, setAvailableStudents] = useState([]);

  // Search bar state for 'add new' modal/student select
  const [studentAddSearch, setStudentAddSearch] = useState('');
  // Search bar state for 'edit' modal/student select
  const [studentEditSearch, setStudentEditSearch] = useState('');
  // Search bar state for attendance marking
  const [attendanceSearch, setAttendanceSearch] = useState('');

  // Edit group modal state
  const [showEditGroup, setShowEditGroup] = useState(false);
  const [editGroupId, setEditGroupId] = useState(null);
  const [editGroupName, setEditGroupName] = useState('');
  const [editGroupMatiere, setEditGroupMatiere] = useState('');
  const [editGroupStudents, setEditGroupStudents] = useState(new Set());
  const [editAvailableStudents, setEditAvailableStudents] = useState([]);
  const [editGroupLoading, setEditGroupLoading] = useState(false);

  // Delete group confirmation
  const [deleteGroupId, setDeleteGroupId] = useState(null);
  const [showDeleteGroupConfirm, setShowDeleteGroupConfirm] = useState(false);

  // Remove student from group confirmation
  const [removeStudentId, setRemoveStudentId] = useState(null);
  const [showRemoveStudentConfirm, setShowRemoveStudentConfirm] = useState(false);

  const { user } = useContext(AuthContext);
  const instructorId = user.id;

  useEffect(() => { initializeData(); }, []);

  const initializeData = async () => {
    setInitialLoading(true);
    setConnectionError('');
    try {
      await Promise.all([
        fetchUsers(),
        fetchMatiers(),
        fetchMessageTemplates(),
        fetchGroups()
      ]);
    } catch (error) {
      setConnectionError('تعذر الاتصال بالخادم. الرجاء التأكد من تشغيل الخادم الخلفي');
    } finally {
      setInitialLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await axiosInstance.get('/absences/users');
      if (response.data.success) setUsers(response.data.data);
    } catch (error) {
      toast.error('فشل في جلب الطلاب من الخادم');
      throw error;
    }
  };

  const fetchMatiers = async () => {
    try {
      const response = await axiosInstance.get('/users/matiers');
      if (response.data.success) setMatiers(response.data.data);
    } catch (error) {
      toast.error('فشل في جلب المواد من الخادم');
      throw error;
    }
  };

  const fetchMessageTemplates = async () => {
    try {
      const response = await axiosInstance.get('/absences/templates');
      if (response.data.success) setMessageTemplates(response.data.data);
    } catch (error) {
      toast.error('فشل في جلب قوالب الرسائل من الخادم');
      throw error;
    }
  };

  const fetchGroups = async () => {
    try {
      const response = await axiosInstance.get(`/groups/instructor/${instructorId}`);
      if (response.data.success) setGroups(response.data.data);
    } catch (error) {
      toast.error('فشل في جلب المجموعات من الخادم');
      throw error;
    }
  };

  const fetchAvailableStudents = async (matiere) => {
    try {
      const response = await axiosInstance.get(`/groups/available-students/${matiere}`);
      if (response.data.success) setAvailableStudents(response.data.data);
    } catch (error) {
      toast.error('فشل في جلب الطلاب المتاحين');
    }
  };

  // For edit group modal: fetch available students for the matiere, including current group students
  const fetchEditAvailableStudents = async (matiere, groupId) => {
    try {
      const response = await axiosInstance.get(`/groups/available-students/${matiere}?includeGroup=${groupId}`);
      if (response.data.success) setEditAvailableStudents(response.data.data);
    } catch (error) {
      toast.error('فشل في جلب الطلاب المتاحين');
    }
  };

  const selectGroup = async (group) => {
    setSelectedGroup(group);
    setSelectedMatier(group.matiere);
    setPresentStudents(new Set());
    setAttendanceSearch(''); // clear search when open
    setCurrentView('attendance');
    try {
      const response = await axiosInstance.get(`/groups/${group._id}`);
      if (response.data.success) setSelectedGroup(response.data.data);
    } catch (error) {
      toast.error('فشل في جلب بيانات المجموعة');
    }
  };

  const toggleStudentPresence = (studentId) => {
    setPresentStudents(prev => {
      const newSet = new Set(prev);
      if (newSet.has(studentId)) newSet.delete(studentId);
      else newSet.add(studentId);
      return newSet;
    });
  };

  const toggleStudentForGroup = (studentId) => {
    setSelectedStudentsForGroup(prev => {
      const newSet = new Set(prev);
      if (newSet.has(studentId)) newSet.delete(studentId);
      else newSet.add(studentId);
      return newSet;
    });
  };

  // For edit group modal
  const toggleStudentForEditGroup = (studentId) => {
    setEditGroupStudents(prev => {
      const newSet = new Set(prev);
      if (newSet.has(studentId)) newSet.delete(studentId);
      else newSet.add(studentId);
      return newSet;
    });
  };

  const createGroup = async () => {
    if (!newGroupName.trim() || !newGroupMatiere || selectedStudentsForGroup.size === 0) {
      toast.error('يرجى ملء جميع الحقول واختيار طالب واحد على الأقل');
      return;
    }
    try {
      const response = await axiosInstance.post('/groups', {
        name: newGroupName.trim(),
        matiere: newGroupMatiere,
        studentIds: Array.from(selectedStudentsForGroup),
        instructorId
      });
      if (response.data.success) {
        toast.success('تم إنشاء المجموعة بنجاح');
        setNewGroupName('');
        setNewGroupMatiere('');
        setSelectedStudentsForGroup(new Set());
        setShowCreateGroup(false);
        fetchGroups();
        setStudentAddSearch('');
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'فشل في إنشاء المجموعة');
    }
  };

  // Edit group handlers
  const openEditGroupModal = async (group) => {
    setEditGroupId(group._id);
    setEditGroupName(group.name);
    setEditGroupMatiere(group.matiere);
    setEditGroupStudents(new Set(group.students.map(s => s._id)));
    setShowEditGroup(true);
    setStudentEditSearch('');
    await fetchEditAvailableStudents(group.matiere, group._id);
  };

  const handleEditGroupMatiereChange = async (matiere) => {
    setEditGroupMatiere(matiere);
    setEditGroupStudents(new Set()); // Clear selection on matiere change
    if (matiere && editGroupId) {
      await fetchEditAvailableStudents(matiere, editGroupId);
    }
  };

  const saveEditGroup = async () => {
    if (!editGroupName.trim() || !editGroupMatiere || editGroupStudents.size === 0) {
      toast.error('يرجى ملء جميع الحقول واختيار طالب واحد على الأقل');
      return;
    }
    setEditGroupLoading(true);
    try {
      const response = await axiosInstance.put(`/groups/${editGroupId}`, {
        name: editGroupName.trim(),
        matiere: editGroupMatiere,
        studentIds: Array.from(editGroupStudents),
        instructorId
      });
      if (response.data.success) {
        toast.success('تم تعديل المجموعة بنجاح');
        setShowEditGroup(false);
        setEditGroupId(null);
        setEditGroupName('');
        setEditGroupMatiere('');
        setEditGroupStudents(new Set());
        setEditAvailableStudents([]);
        fetchGroups();
        setStudentEditSearch('');
        // If the edited group is currently selected, update it
        if (selectedGroup && selectedGroup._id === editGroupId) {
          selectGroup({ ...selectedGroup, name: editGroupName, matiere: editGroupMatiere });
        }
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'فشل في تعديل المجموعة');
    } finally {
      setEditGroupLoading(false);
    }
  };

  // Delete group handlers
  const openDeleteGroupConfirm = (groupId) => {
    setDeleteGroupId(groupId);
    setShowDeleteGroupConfirm(true);
  };

  const handleDeleteGroup = async () => {
    if (!deleteGroupId) return;
    try {
      const response = await axiosInstance.delete(`/groups/${deleteGroupId}`);
      if (response.data.success) {
        toast.success('تم حذف المجموعة بنجاح');
        setShowDeleteGroupConfirm(false);
        setDeleteGroupId(null);
        fetchGroups();
        // If the deleted group is currently selected, reset view
        if (selectedGroup && selectedGroup._id === deleteGroupId) {
          setCurrentView('groups');
          setSelectedGroup(null);
          setPresentStudents(new Set());
        }
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'فشل في حذف المجموعة');
    }
  };

  // Remove student from group handlers
  const openRemoveStudentConfirm = (studentId) => {
    setRemoveStudentId(studentId);
    setShowRemoveStudentConfirm(true);
  };

  const handleRemoveStudentFromGroup = async () => {
    if (!selectedGroup || !removeStudentId) return;
    try {
      const response = await axiosInstance.put(`/groups/${selectedGroup._id}/remove-student`, {
        studentId: removeStudentId
      });
      if (response.data.success) {
        toast.success('تم حذف الطالب من المجموعة');
        // Refresh group data
        selectGroup(selectedGroup);
        setShowRemoveStudentConfirm(false);
        setRemoveStudentId(null);
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'فشل في حذف الطالب من المجموعة');
    }
  };

  const sendAbsenceAlerts = async () => {
    if (!selectedGroup) {
      toast.error('يرجى اختيار مجموعة');
      return;
    }
    const allStudentIds = selectedGroup.students.map(s => s._id);
    const absentStudentIds = allStudentIds.filter(id => !presentStudents.has(id));
    if (absentStudentIds.length === 0) {
      toast.error('جميع الطلاب حاضرون. لا توجد تنبيهات للإرسال.');
      return;
    }
    setLoading(true);
    try {
      const absenceData = {
        studentIds: absentStudentIds,
        instructorId,
        matier: selectedGroup.matiere,
        alertMessage: messageType === 'custom' ? customMessage : undefined,
        messageType,
        sendNotification: true
      };
      const response = await axiosInstance.post('/absences', absenceData);
      if (response.data && response.data.success) {
        toast.success(`تم إرسال ${absentStudentIds.length} تنبيه غياب بنجاح`);
        setPresentStudents(new Set());
        setLessonTitle('');
        setCustomMessage('');
        setCurrentView('groups');
        setSelectedGroup(null);
        setAttendanceSearch('');
      } else {
        toast.error('فشل في إرسال تنبيهات الغياب');
      }
    } catch (error) {
      toast.error('فشل في إرسال تنبيهات الغياب إلى الخادم');
    } finally {
      setLoading(false);
    }
  };

  const getSelectedTemplate = () => {
    return messageTemplates.find(template => template.key === messageType);
  };

  const retryConnection = () => { initializeData(); };

  // Responsive CSS
  const responsiveStyles = `
    body, input, select, textarea, button { font-family: 'Cairo', 'Tajawal', Arial, sans-serif; }
    .adminalert-main {
      padding: 0.5rem 0.5rem 0.5rem 0.5rem !important;
      max-width: 900px !important;
      margin-right: 200px !important;
      transition: margin-right 0.3s, max-width 0.3s;
      width: 100%;
      box-sizing: border-box;
    }
    .adminalert-header {
      padding: 0.7rem 1rem !important;
      margin-bottom: 1rem !important;
      width: 100%;
      box-sizing: border-box;
    }
    .adminalert-groups-grid {
      grid-template-columns: repeat(auto-fit, minmax(220px, 1fr)) !important;
      gap: 0.7rem !important;
      width: 100%;
      box-sizing: border-box;
    }
    .adminalert-attendance-grid {
      grid-template-columns: 1.1fr 0.9fr !important;
      gap: 1.1rem !important;
      width: 100%;
      box-sizing: border-box;
    }
    .adminalert-students-list {
      grid-template-columns: 1fr !important;
      gap: 0.5rem !important;
      width: 100%;
      box-sizing: border-box;
    }
    .adminalert-modal {
      padding: 1rem !important;
      max-width: 95vw !important;
      width: 100%;
      box-sizing: border-box;
    }
    .adminalert-sidebar-fix {
      width: 180px;
      min-width: 180px;
      max-width: 180px;
      z-index: 2;
      background: #fff;
      box-shadow: 0 2px 12px rgba(0,0,0,0.06);
      height: 100vh;
      position: fixed;
      right: 0;
      top: 0;
      transition: width 0.3s, min-width 0.3s, max-width 0.3s, height 0.3s;
    }
    @media (max-width: 1200px) {
      .adminalert-main { max-width: 100vw !important; margin-right: 180px !important; }
    }
    @media (max-width: 900px) {
      .adminalert-main {
        margin-right: 0 !important;
        max-width: 100vw !important;
        width: 100vw !important;
        padding: 0 !important;
        box-sizing: border-box !important;
      }
      .adminalert-attendance-grid { grid-template-columns: 1fr !important; }
      .adminalert-sidebar-fix { margin:0 }
    }
    @media (max-width: 700px) {
      .adminalert-main {
        padding: 0.2rem !important;
        max-width: 100vw !important;
        width: 100vw !important;
        margin: 0 !important;
        box-sizing: border-box !important;
      .adminalert-main { max-width: 100vw !important; margin-right: 70px !important; }

      }
      .adminalert-header { padding: 0.3rem !important; width: 100vw !important; box-sizing: border-box !important; }
      .adminalert-modal { padding: 0.2rem !important; width: 100vw !important; box-sizing: border-box !important; }
      .adminalert-groups-grid { grid-template-columns: 1fr !important; width: 100vw !important; box-sizing: border-box !important; }
      .adminalert-attendance-grid { grid-template-columns: 1fr !important; width: 100vw !important; box-sizing: border-box !important; }
    }
    @media (max-width: 500px) {
      html, body, #root, .adminalert-root-outer {
        width: 100vw !important;
        min-width: 100vw !important;
        max-width: 100vw !important;
        margin: 0 !important;
        padding: 0 !important;
        box-sizing: border-box !important;
      }
      .adminalert-main {
        padding: 0 !important;
        margin: 0 !important;
        max-width: 100vw !important;
        width: 100vw !important;
        min-width: 100vw !important;
        box-sizing: border-box !important;
        display: block !important;
      }
      .adminalert-header {
        padding: 0.1rem !important;
        width: 100vw !important;
        box-sizing: border-box !important;
      }
      .adminalert-modal {
        padding: 0.1rem !important;
        width: 100vw !important;
        box-sizing: border-box !important;
      }
      .adminalert-groups-grid {
        grid-template-columns: 1fr !important;
        width: 100vw !important;
        box-sizing: border-box !important;
      }
      .adminalert-attendance-grid {
        grid-template-columns: 1fr !important;
        width: 100vw !important;
        box-sizing: border-box !important;
      }
    }
  `;

  // Add inline style fix for mobile screens (width <= 500px)
  // This will override the main container's width, margin, and padding for mobile
  const isMobile = typeof window !== "undefined" && window.innerWidth <= 500;
  const rootOuterStyle = isMobile
    ? {
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #e0e7ff 0%, #c7d2fe 100%)',
        width: '100vw',
        maxWidth: '100vw',
        minWidth: '100vw',
        margin: 0,
        padding: 0,
        boxSizing: 'border-box',
      }
    : {
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #e0e7ff 0%, #c7d2fe 100%)',
        width: '100vw',
        maxWidth: '100vw',
        margin: 0,
        padding: 0,
        boxSizing: 'border-box',
      };

  if (initialLoading) {
    return (
      <div dir="rtl" style={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #e0e7ff 0%, #c7d2fe 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <div style={{
          background: '#fff',
          borderRadius: '0.7rem',
          boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
          padding: '1.2rem 2rem',
          textAlign: 'center',
          width: '260px'
        }}>
          <div className="loader" style={{
            margin: '0 auto 1rem auto',
            border: '3px solid #e0e7ff',
            borderTop: '3px solid #6366f1',
            borderRadius: '50%',
            width: '32px',
            height: '32px',
            animation: 'spin 1s linear infinite'
          }} />
          <h2 style={{ fontWeight: 700, fontSize: '1.05rem', marginBottom: '0.3rem' }}>جاري التحميل...</h2>
          <p style={{ color: '#64748b', fontSize: '0.93rem' }}>يتم تحميل النظام</p>
          <style>{`@keyframes spin { 100% { transform: rotate(360deg); } }`}</style>
        </div>
      </div>
    );
  }

  if (connectionError) {
    return (
      <div dir="rtl" style={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #e0e7ff 0%, #c7d2fe 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <div style={{
          background: '#fff',
          borderRadius: '0.7rem',
          boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
          padding: '1.2rem 2rem',
          textAlign: 'center',
          width: '260px'
        }}>
          <div style={{ fontSize: '2rem', color: '#ef4444', marginBottom: '0.7rem' }}>⚠️</div>
          <h2 style={{ fontWeight: 700, fontSize: '1.05rem', color: '#b91c1c', marginBottom: '0.3rem' }}>خطأ في الاتصال</h2>
          <p style={{ color: '#64748b', marginBottom: '0.7rem', fontSize: '0.93rem' }}>{connectionError}</p>
          <button
            onClick={retryConnection}
            style={{
              background: '#6366f1',
              color: '#fff',
              border: 'none',
              borderRadius: '0.4rem',
              padding: '0.4rem 1.1rem',
              fontWeight: 600,
              cursor: 'pointer',
              width: '100%',
              fontSize: '0.98rem'
            }}
          >
            إعادة المحاولة
          </button>
        </div>
      </div>
    );
  }

  // Modal overlay style for full width/height, centered content
  const modalOverlayStyle = {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    width: '100vw',
    height: '100vh',
    background: 'rgba(0,0,0,0.3)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1100
  };

  // Modal content style for full width/height, centered content
  const modalContentStyle = {
    background: '#fff',
    borderRadius: '0.7rem',
    padding: isMobile ? '0.1rem' : '1rem',
    width: '100vw',
    maxWidth: '100vw',
    minWidth: '100vw',
    height: '100vh',
    maxHeight: '100vh',
    minHeight: '100vh',
    overflowY: 'auto',
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center'
  };

  // For smaller modals (confirmation), center and full width/height
  const modalContentSmallStyle = {
    ...modalContentStyle,
    maxWidth: '100vw',
    width: '100vw',
    minWidth: '100vw',
    maxHeight: '100vh',
    minHeight: '100vh',
    height: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  };

  return (
    <div
      className="adminalert-root-outer"
      dir="rtl"
      style={rootOuterStyle}
    >
      <style>{responsiveStyles}</style>
      <div
        style={{
          display: 'flex',
          flexDirection: 'row',
          minHeight: '100vh',
          position: 'relative',
          width: '100%',
          boxSizing: 'border-box',
          margin: 0,
          padding: 0
        }}
      >   
        <Sidebar2 />
        <main
          className="adminalert-main"
          style={
            isMobile
              ? {
                  flex: 1,
                  padding: 0,
                  margin: 0,
                  maxWidth: '100vw',
                  width: '100vw',
                  boxSizing: 'border-box'
                }
              : {
                  flex: 1,
                  padding: '0.5rem',
                  maxWidth: 'calc(100vw - 180px)',
                  marginRight: '180px',
                  boxSizing: 'border-box',
                  width: '100%'
                }
          }
        >
          {/* Header */}
          <div
            className="adminalert-header"
            style={
              isMobile
                ? {
                    textAlign: 'center',
                    marginBottom: '1rem',
                    background: '#fff',
                    borderRadius: '0.7rem',
                    padding: '0.1rem',
                    boxShadow: '0 2px 12px rgba(0,0,0,0.06)'
                  }
                : {
                    textAlign: 'center',
                    marginBottom: '1rem',
                    background: '#fff',
                    borderRadius: '0.7rem',
                    padding: '0.7rem 1rem',
                    boxShadow: '0 2px 12px rgba(0,0,0,0.06)'
                  }
            }
          >
            <h1 style={{
              fontSize: '1.45rem',
              fontWeight: 700,
              background: 'linear-gradient(90deg, #6366f1, #4f46e5)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              marginBottom: '0.2rem'
            }}>
              نظام إدارة الغياب
            </h1>
            <p style={{ fontSize: '1rem', color: '#64748b', marginBottom: '0.5rem' }}>
              {currentView === 'groups'
                ? 'اختر مجموعة لبدء تسجيل الحضور والغياب'
                : `تسجيل الحضور - مجموعة ${selectedGroup?.name} - ${selectedGroup?.matiere}`
              }
            </p>
            <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'center', flexWrap: 'wrap' }}>
              {currentView === 'attendance' && (
                <button
                  onClick={() => {
                    setCurrentView('groups');
                    setSelectedGroup(null);
                    setPresentStudents(new Set());
                    setAttendanceSearch('');
                  }}
                  style={{
                    background: '#64748b',
                    color: '#fff',
                    border: 'none',
                    borderRadius: '0.4rem',
                    padding: '0.4rem 1.1rem',
                    fontWeight: 600,
                    cursor: 'pointer',
                    fontSize: '0.98rem'
                  }}
                >
                  ← العودة للمجموعات
                </button>
              )}
              <button
                onClick={() => setShowCreateGroup(true)}
                style={{
                  background: '#16a34a',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '0.4rem',
                  padding: '0.4rem 1.1rem',
                  fontWeight: 600,
                  cursor: 'pointer',
                  fontSize: '0.98rem'
                }}
              >
                + إنشاء مجموعة جديدة
              </button>
            </div>
          </div>

          {/* Groups View */}
          {currentView === 'groups' && (
            <div style={{
              background: '#fff',
              borderRadius: '0.7rem',
              boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
              padding: '1rem'
            }}>
              <h2 style={{
                fontSize: '1.1rem',
                fontWeight: 700,
                marginBottom: '1rem',
                display: 'flex',
                alignItems: 'center',
                gap: '0.4rem'
              }}>
                <span>👥</span>
                المجموعات ({groups.length})
              </h2>
              {groups.length === 0 ? (
                <div style={{
                  textAlign: 'center',
                  color: '#64748b',
                  padding: '1.5rem 0',
                  fontSize: '0.98rem'
                }}>
                  <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>📚</div>
                  <p>لا توجد مجموعات حتى الآن</p>
                  <p>انقر على "إنشاء مجموعة جديدة" لبدء إنشاء مجموعاتك</p>
                </div>
              ) : (
                <div
                  className="adminalert-groups-grid"
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
                    gap: '0.7rem'
                  }}
                >
                  {groups.map((group) => (
                    <div
                      key={group._id}
                      style={{
                        border: '1.5px solid #e5e7eb',
                        borderRadius: '0.7rem',
                        padding: '1rem',
                        cursor: 'pointer',
                        transition: 'all 0.2s',
                        background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
                        position: 'relative'
                      }}
                      onClick={() => selectGroup(group)}
                      onMouseEnter={e => {
                        e.currentTarget.style.borderColor = '#6366f1';
                        e.currentTarget.style.transform = 'translateY(-2px)';
                        e.currentTarget.style.boxShadow = '0 4px 16px rgba(99,102,241,0.10)';
                      }}
                      onMouseLeave={e => {
                        e.currentTarget.style.borderColor = '#e5e7eb';
                        e.currentTarget.style.transform = 'translateY(0)';
                        e.currentTarget.style.boxShadow = 'none';
                      }}
                    >
                      {/* Group actions (edit/delete) */}
                      <div style={{
                        position: 'absolute',
                        top: '0.5rem',
                        left: '0.5rem',
                        display: 'flex',
                        gap: '0.3rem',
                        zIndex: 2
                      }}
                        onClick={e => e.stopPropagation()}
                      >
                        <button
                          title="تعديل المجموعة"
                          onClick={() => openEditGroupModal(group)}
                          style={{
                            background: '#fbbf24',
                            color: '#fff',
                            border: 'none',
                            borderRadius: '0.3rem',
                            padding: '0.2rem 0.5rem',
                            fontWeight: 700,
                            fontSize: '0.9rem',
                            cursor: 'pointer'
                          }}
                        >✏️</button>
                        <button
                          title="حذف المجموعة"
                          onClick={() => openDeleteGroupConfirm(group._id)}
                          style={{
                            background: '#dc2626',
                            color: '#fff',
                            border: 'none',
                            borderRadius: '0.3rem',
                            padding: '0.2rem 0.5rem',
                            fontWeight: 700,
                            fontSize: '0.9rem',
                            cursor: 'pointer'
                          }}
                        >🗑️</button>
                      </div>
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        marginBottom: '0.7rem'
                      }}>
                        <h3 style={{
                          fontSize: '1.05rem',
                          fontWeight: 700,
                          color: '#1e293b',
                          margin: 0
                        }}>
                          {group.name}
                        </h3>
                        <span style={{
                          background: '#6366f1',
                          color: '#fff',
                          borderRadius: '50%',
                          width: '1.7rem',
                          height: '1.7rem',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontWeight: 700,
                          fontSize: '0.98rem'
                        }}>
                          {group.students.length}
                        </span>
                      </div>
                      <div style={{
                        background: '#e0e7ff',
                        borderRadius: '0.4rem',
                        padding: '0.4rem 0.7rem',
                        marginBottom: '0.7rem',
                        fontSize: '0.95rem',
                        color: '#3730a3',
                        fontWeight: 600
                      }}>
                        المادة: {group.matiere}
                      </div>
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.3rem',
                        color: '#16a34a',
                        fontSize: '0.93rem'
                      }}>
                        <span>✅</span>
                        <span>{group.students.length} طالب</span>
                      </div>
                      <div style={{
                        marginTop: '0.7rem',
                        padding: '0.4rem',
                        background: 'rgba(99,102,241,0.08)',
                        borderRadius: '0.4rem',
                        textAlign: 'center',
                        color: '#6366f1',
                        fontWeight: 600,
                        fontSize: '0.93rem'
                      }}>
                        انقر لبدء تسجيل الحضور
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Attendance View */}
          {currentView === 'attendance' && selectedGroup && (
            <div
              className="adminalert-attendance-grid"
              style={{
                display: 'grid',
                gridTemplateColumns: window.innerWidth <= 900 ? '1fr' : '1.1fr 0.9fr',
                gap: window.innerWidth <= 700 ? '0.5rem' : '1.1rem',
                alignItems: 'start'
              }}
            >
              {/* Menu/Setup (right) */}
              <div style={{
                background: '#fff',
                borderRadius: '0.7rem',
                boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
                padding: '1rem',
                minWidth: 0,
                position: window.innerWidth > 900 ? 'sticky' : 'static',
                top: '1rem'
              }}>
                <h2 style={{
                  fontSize: '1.05rem',
                  fontWeight: 700,
                  marginBottom: '1rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.4rem'
                }}>
                  <span>📚</span>
                  إعداد الدرس
                </h2>
                <div style={{ marginBottom: '0.7rem' }}>
                  <label style={{ fontWeight: 700, display: 'block', marginBottom: '0.2rem', fontSize: '0.93rem' }}>
                    المجموعة المحددة
                  </label>
                  <div style={{
                    background: '#f1f5f9',
                    padding: '0.4rem 0.7rem',
                    borderRadius: '0.4rem',
                    border: '1.5px solid #e0e7ff',
                    fontSize: '0.93rem'
                  }}>
                    <div style={{ fontWeight: 700, color: '#1e293b' }}>{selectedGroup.name}</div>
                    <div style={{ color: '#64748b', fontSize: '0.85rem' }}>{selectedGroup.matiere}</div>
                  </div>
                </div>
                <div style={{ marginBottom: '0.7rem' }}>
                  <label style={{ fontWeight: 700, display: 'block', marginBottom: '0.2rem', fontSize: '0.93rem' }}>
                    نوع الرسالة
                  </label>
                  <select
                    value={messageType}
                    onChange={e => setMessageType(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '0.4rem',
                      borderRadius: '0.4rem',
                      border: '1.5px solid #cbd5e1',
                      fontSize: '0.98rem'
                    }}
                  >
                    <option value="absence_alert">تنبيه غياب عادي</option>
                    <option value="custom">رسالة مخصصة</option>
                  </select>
                </div>
                {messageType === 'custom' && (
                  <div style={{ marginBottom: '0.7rem' }}>
                    <label style={{ fontWeight: 700, display: 'block', marginBottom: '0.2rem', fontSize: '0.93rem' }}>
                      رسالة مخصصة
                    </label>
                    <textarea
                      placeholder="أدخل رسالتك المخصصة"
                      value={customMessage}
                      onChange={e => setCustomMessage(e.target.value)}
                      rows={2}
                      style={{
                        width: '100%',
                        padding: '0.4rem',
                        borderRadius: '0.4rem',
                        border: '1.5px solid #cbd5e1',
                        fontSize: '0.98rem',
                        resize: 'vertical'
                      }}
                    />
                  </div>
                )}
                {messageType !== 'custom' && getSelectedTemplate() && (
                  <div style={{
                    background: '#f0f9ff',
                    borderRadius: '0.4rem',
                    padding: '0.6rem',
                    marginBottom: '0.7rem',
                    border: '1.5px solid #e0f2fe',
                    fontSize: '0.93rem'
                  }}>
                    <strong style={{ color: '#0369a1' }}>معاينة الرسالة:</strong>
                    <div style={{ marginTop: '0.3rem', color: '#0c4a6e' }}>
                      {getSelectedTemplate()?.template
                        .replace('{studentName}', '[اسم الطالب]')
                        .replace('{matier}', selectedGroup.matiere)
                      }
                    </div>
                  </div>
                )}
                <div style={{
                  background: '#f8fafc',
                  borderRadius: '0.4rem',
                  padding: '0.6rem',
                  marginBottom: '0.9rem',
                  border: '1.5px solid #e2e8f0',
                  fontSize: '0.93rem'
                }}>
                  <div style={{ fontWeight: 700, marginBottom: '0.2rem' }}>ملخص الحضور:</div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.1rem' }}>
                    <span>إجمالي الطلاب:</span>
                    <span style={{ fontWeight: 700 }}>{selectedGroup.students.length}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.1rem' }}>
                    <span style={{ color: '#16a34a' }}>الحاضرون:</span>
                    <span style={{ fontWeight: 700, color: '#16a34a' }}>{presentStudents.size}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ color: '#dc2626' }}>الغائبون:</span>
                    <span style={{ fontWeight: 700, color: '#dc2626' }}>
                      {selectedGroup.students.length - presentStudents.size}
                    </span>
                  </div>
                </div>
                <button
                  onClick={sendAbsenceAlerts}
                  disabled={loading || selectedGroup.students.length - presentStudents.size === 0}
                  style={{
                    width: '100%',
                    background: loading ? '#a5b4fc' :
                      (selectedGroup.students.length - presentStudents.size === 0) ? '#9ca3af' : '#dc2626',
                    color: '#fff',
                    border: 'none',
                    borderRadius: '0.4rem',
                    padding: '0.7rem',
                    fontWeight: 700,
                    fontSize: '1rem',
                    cursor: loading || (selectedGroup.students.length - presentStudents.size === 0) ? 'not-allowed' : 'pointer'
                  }}
                >
                  {loading ? 'جاري الإرسال...' :
                    selectedGroup.students.length - presentStudents.size === 0 ? 'جميع الطلاب حاضرون' :
                      `إرسال تنبيه لـ ${selectedGroup.students.length - presentStudents.size} غائب`}
                </button>
              </div>
              {/* Students List (left) */}
              <div
                className="adminalert-students-list"
                style={{
                  background: '#fff',
                  borderRadius: '0.7rem',
                  boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
                  padding: '1rem',
                  minWidth: 0,
                  marginTop: window.innerWidth <= 900 ? '1rem' : 0
                }}
              >
                <h2 style={{
                  fontSize: '1.05rem',
                  fontWeight: 700,
                  marginBottom: '0.7rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.4rem'
                }}>
                  <span>✅</span>
                  قائمة الطلاب
                </h2>
                {/* Attendance mark/search bar */}
                <div style={{ marginBottom: '1rem' }}>
                  <input
                    type="text"
                    placeholder="بحث الطالب (اسم أو إيميل)..."
                    value={attendanceSearch}
                    onChange={e => setAttendanceSearch(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '0.35rem 0.6rem',
                      borderRadius: '0.35rem',
                      border: '1.2px solid #cbd5e1',
                      fontSize: '0.96rem'
                    }}
                  />
                </div>
                <div
                  className="adminalert-students-list"
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '1fr',
                    gap: '0.5rem'
                  }}
                >
                  {(attendanceSearch.trim().length > 0
                    ? selectedGroup.students.filter(
                        student =>
                          student.username &&
                          student.username.toLowerCase().includes(attendanceSearch.trim().toLowerCase())
                          || (student.email && student.email.toLowerCase().includes(attendanceSearch.trim().toLowerCase()))
                      )
                    : selectedGroup.students
                  ).map((student) => (
                    <div
                      key={student._id}
                      style={{
                        border: presentStudents.has(student._id) ? '2px solid #16a34a' : '1.5px solid #e5e7eb',
                        background: presentStudents.has(student._id) ? '#f0fdf4' : '#fff',
                        borderRadius: '0.5rem',
                        padding: '0.6rem 0.7rem',
                        cursor: 'pointer',
                        transition: 'all 0.15s',
                        position: 'relative'
                      }}
                      onClick={() => toggleStudentPresence(student._id)}
                    >
                      {/* Remove student from group button */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.7rem' }}>
                        <div style={{
                          width: '1.5rem',
                          height: '1.5rem',
                          borderRadius: '50%',
                          background: presentStudents.has(student._id) ? '#16a34a' : '#e5e7eb',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: '#fff',
                          fontWeight: 700,
                          fontSize: '1rem'
                        }}>
                          {presentStudents.has(student._id) ? '✓' : '?'}
                        </div>
                        <div style={{ flex: 1 }}>
                          <div style={{
                            fontWeight: 700,
                            fontSize: '0.98rem',
                            color: '#1e293b',
                            marginBottom: '0.1rem'
                          }}>
                            {student.username}
                          </div>
                          <div style={{
                            fontSize: '0.85rem',
                            color: '#64748b',
                            marginBottom: '0.1rem'
                          }}>
                            {student.email}
                          </div>
                          <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.2rem',
                            fontSize: '0.8rem'
                          }}>
                            {student.parentPhoneNumber ? (
                              <>
                                <span style={{ color: '#16a34a' }}>📱</span>
                                <span style={{ color: '#16a34a' }}>ولي الأمر</span>
                              </>
                            ) : (
                              <>
                                <span style={{ color: '#f59e0b' }}>⚠️</span>
                                <span style={{ color: '#d97706' }}>لا يوجد رقم ولي الأمر</span>
                              </>
                            )}
                          </div>
                        </div>
                        <div style={{
                          background: presentStudents.has(student._id) ? '#16a34a' : '#dc2626',
                          color: '#fff',
                          borderRadius: '0.3rem',
                          padding: '0.2rem 0.7rem',
                          fontWeight: 700,
                          fontSize: '0.85rem'
                        }}>
                          {presentStudents.has(student._id) ? 'حاضر' : 'غائب'}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </main>
      </div>

      {/* Create Group Modal */}
      {showCreateGroup && (
        <div style={modalOverlayStyle}>
          <div
            className="adminalert-modal adminalert-modal-content"
            style={modalContentStyle}
          >
            <h2 style={{
              fontSize: '1.1rem',
              fontWeight: 700,
              marginBottom: '1rem',
              textAlign: 'center'
            }}>
              إنشاء مجموعة جديدة
            </h2>
            <div style={{ marginBottom: '0.7rem' }}>
              <label style={{ fontWeight: 700, display: 'block', marginBottom: '0.2rem', fontSize: '0.93rem' }}>
                اسم المجموعة
              </label>
              <input
                type="text"
                placeholder="مثال: المجموعة الأولى، مجموعة الصباح..."
                value={newGroupName}
                onChange={e => setNewGroupName(e.target.value)}
                style={{
                  width: '100%',
                  padding: '0.4rem',
                  borderRadius: '0.4rem',
                  border: '1.5px solid #cbd5e1',
                  fontSize: '0.98rem'
                }}
              />
            </div>
            <div style={{ marginBottom: '0.7rem' }}>
              <label style={{ fontWeight: 700, display: 'block', marginBottom: '0.2rem', fontSize: '0.93rem' }}>
                المادة
              </label>
              <select
                value={newGroupMatiere}
                onChange={e => {
                  setNewGroupMatiere(e.target.value);
                  if (e.target.value) fetchAvailableStudents(e.target.value);
                  setStudentAddSearch('');
                }}
                style={{
                  width: '100%',
                  padding: '0.4rem',
                  borderRadius: '0.4rem',
                  border: '1.5px solid #cbd5e1',
                  fontSize: '0.98rem'
                }}
              >
                <option value="">اختر المادة</option>
                {matiers.map((matier) => (
                  <option key={matier} value={matier}>{matier}</option>
                ))}
              </select>
            </div>
            {newGroupMatiere && (
              <div style={{ marginBottom: '1rem' }}>
                <label style={{ fontWeight: 700, display: 'block', marginBottom: '0.2rem', fontSize: '0.93rem' }}>
                  الطلاب المتاحون ({selectedStudentsForGroup.size} محدد)
                </label>
                {/* Search bar for add student modal */}
                <div style={{ marginBottom: '0.5rem' }}>
                  <input
                    type="text"
                    placeholder="بحث الطالب (اسم أو إيميل)..."
                    value={studentAddSearch}
                    autoFocus
                    onChange={e => setStudentAddSearch(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '0.3rem 0.5rem',
                      borderRadius: '0.35rem',
                      border: '1.2px solid #cbd5e1',
                      fontSize: '0.96rem'
                    }}
                  />
                </div>
                <div style={{
                  maxHeight: '500px',
                  overflowY: 'auto',
                  border: '1.5px solid #cbd5e1',
                  borderRadius: '0.4rem',
                  padding: '0.5rem'
                }}>
                  {availableStudents.length === 0 ? (
                    <p style={{
                      color: '#64748b',
                      textAlign: 'center',
                      padding: '1rem',
                      fontSize: '0.98rem'
                    }}>
                      لا يوجد طلاب متاحون لهذه المادة أو جميع الطلاب موزعون على مجموعات أخرى
                    </p>
                  ) : (
                    <div
                      className="adminalert-students-list"
                      style={{
                        display: 'grid',
                        gridTemplateColumns: '1fr',
                        gap: '0.3rem'
                      }}
                    >
                      {(studentAddSearch.trim().length > 0
                        ? availableStudents.filter(
                            s =>
                              (s.username && s.username.toLowerCase().includes(studentAddSearch.trim().toLowerCase()))
                              || (s.email && s.email.toLowerCase().includes(studentAddSearch.trim().toLowerCase()))
                          )
                        : availableStudents
                      ).map((student) => (
                        <div
                          key={student._id}
                          onClick={() => toggleStudentForGroup(student._id)}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.5rem',
                            padding: '0.4rem',
                            borderRadius: '0.3rem',
                            cursor: 'pointer',
                            background: selectedStudentsForGroup.has(student._id) ? '#f0f9ff' : '#f8fafc',
                            border: selectedStudentsForGroup.has(student._id) ? '1.5px solid #6366f1' : '1.5px solid #e2e8f0'
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={selectedStudentsForGroup.has(student._id)}
                            onChange={() => toggleStudentForGroup(student._id)}
                            style={{
                              accentColor: '#6366f1',
                              width: '1rem',
                              height: '1rem'
                            }}
                            onClick={e => e.stopPropagation()}
                          />
                          <div>
                            <div style={{ fontWeight: 700, fontSize: '0.93rem' }}>{student.username}</div>
                            <div style={{ fontSize: '0.8rem', color: '#64748b' }}>{student.email}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}
            <div style={{
              display: 'flex',
              gap: '0.5rem',
              justifyContent: 'center',
              flexWrap: 'wrap'
            }}>
              <button
                onClick={() => {
                  setShowCreateGroup(false);
                  setNewGroupName('');
                  setNewGroupMatiere('');
                  setSelectedStudentsForGroup(new Set());
                  setAvailableStudents([]);
                  setStudentAddSearch('');
                }}
                style={{
                  background: '#6b7280',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '0.4rem',
                  padding: '0.4rem 1.1rem',
                  fontWeight: 600,
                  cursor: 'pointer',
                  fontSize: '0.98rem'
                }}
              >
                إلغاء
              </button>
              <button
                onClick={createGroup}
                disabled={!newGroupName.trim() || !newGroupMatiere || selectedStudentsForGroup.size === 0}
                style={{
                  background: (newGroupName.trim() && newGroupMatiere && selectedStudentsForGroup.size > 0) ? '#16a34a' : '#9ca3af',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '0.4rem',
                  padding: '0.4rem 1.1rem',
                  fontWeight: 600,
                  cursor: (newGroupName.trim() && newGroupMatiere && selectedStudentsForGroup.size > 0) ? 'pointer' : 'not-allowed',
                  fontSize: '0.98rem'
                }}
              >
                إنشاء المجموعة ({selectedStudentsForGroup.size} طالب)
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Group Modal */}
      {showEditGroup && (
        <div style={modalOverlayStyle}>
          <div
            className="adminalert-modal adminalert-modal-content"
            style={modalContentStyle}
          >
            <h2 style={{
              fontSize: '1.1rem',
              fontWeight: 700,
              marginBottom: '1rem',
              textAlign: 'center'
            }}>
              تعديل المجموعة
            </h2>
            <div style={{ marginBottom: '0.7rem' }}>
              <label style={{ fontWeight: 700, display: 'block', marginBottom: '0.2rem', fontSize: '0.93rem' }}>
                اسم المجموعة
              </label>
              <input
                type="text"
                placeholder="اسم المجموعة"
                value={editGroupName}
                onChange={e => setEditGroupName(e.target.value)}
                style={{
                  width: '100%',
                  padding: '0.4rem',
                  borderRadius: '0.4rem',
                  border: '1.5px solid #cbd5e1',
                  fontSize: '0.98rem'
                }}
              />
            </div>
            <div style={{ marginBottom: '0.7rem' }}>
              <label style={{ fontWeight: 700, display: 'block', marginBottom: '0.2rem', fontSize: '0.93rem' }}>
                المادة
              </label>
              <select
                value={editGroupMatiere}
                onChange={e => {handleEditGroupMatiereChange(e.target.value); setStudentEditSearch('');}}
                style={{
                  width: '100%',
                  padding: '0.4rem',
                  borderRadius: '0.4rem',
                  border: '1.5px solid #cbd5e1',
                  fontSize: '0.98rem'
                }}
              >
                <option value="">اختر المادة</option>
                {matiers.map((matier) => (
                  <option key={matier} value={matier}>{matier}</option>
                ))}
              </select>
            </div>
            {editGroupMatiere && (
              <div style={{ marginBottom: '1rem' }}>
                <label style={{ fontWeight: 700, display: 'block', marginBottom: '0.2rem', fontSize: '0.93rem' }}>
                  الطلاب في المجموعة ({editGroupStudents.size} محدد)
                </label>
                {/* Search bar for edit student modal */}
                <div style={{ marginBottom: '0.5rem' }}>
                  <input
                    type="text"
                    placeholder="بحث الطالب (اسم أو إيميل)..."
                    value={studentEditSearch}
                    autoFocus
                    onChange={e => setStudentEditSearch(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '0.3rem 0.5rem',
                      borderRadius: '0.35rem',
                      border: '1.2px solid #cbd5e1',
                      fontSize: '0.96rem'
                    }}
                  />
                </div>
                <div style={{
                  maxHeight: '500px',
                  overflowY: 'auto',
                  border: '1.5px solid #cbd5e1',
                  borderRadius: '0.4rem',
                  padding: '0.5rem'
                }}>
                  {editAvailableStudents.length === 0 ? (
                    <p style={{
                      color: '#64748b',
                      textAlign: 'center',
                      padding: '1rem',
                      fontSize: '0.98rem'
                    }}>
                      لا يوجد طلاب متاحون لهذه المادة أو جميع الطلاب موزعون على مجموعات أخرى
                    </p>
                  ) : (
                    <div
                      className="adminalert-students-list"
                      style={{
                        display: 'grid',
                        gridTemplateColumns: '1fr',
                        gap: '0.3rem'
                      }}
                    >
                      {(studentEditSearch.trim().length > 0
                        ? editAvailableStudents.filter(
                            s =>
                              (s.username && s.username.toLowerCase().includes(studentEditSearch.trim().toLowerCase()))
                              || (s.email && s.email.toLowerCase().includes(studentEditSearch.trim().toLowerCase()))
                          )
                        : editAvailableStudents
                      ).map((student) => (
                        <div
                          key={student._id}
                          onClick={() => toggleStudentForEditGroup(student._id)}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.5rem',
                            padding: '0.4rem',
                            borderRadius: '0.3rem',
                            cursor: 'pointer',
                            background: editGroupStudents.has(student._id) ? '#f0f9ff' : '#f8fafc',
                            border: editGroupStudents.has(student._id) ? '1.5px solid #6366f1' : '1.5px solid #e2e8f0'
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={editGroupStudents.has(student._id)}
                            onChange={() => toggleStudentForEditGroup(student._id)}
                            style={{
                              accentColor: '#6366f1',
                              width: '1rem',
                              height: '1rem'
                            }}
                            onClick={e => e.stopPropagation()}
                          />
                          <div>
                            <div style={{ fontWeight: 700, fontSize: '0.93rem' }}>{student.username}</div>
                            <div style={{ fontSize: '0.8rem', color: '#64748b' }}>{student.email}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}
            <div style={{
              display: 'flex',
              gap: '0.5rem',
              justifyContent: 'center',
              flexWrap: 'wrap'
            }}>
              <button
                onClick={() => {
                  setShowEditGroup(false);
                  setEditGroupId(null);
                  setEditGroupName('');
                  setEditGroupMatiere('');
                  setEditGroupStudents(new Set());
                  setEditAvailableStudents([]);
                  setStudentEditSearch('');
                }}
                style={{
                  background: '#6b7280',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '0.4rem',
                  padding: '0.4rem 1.1rem',
                  fontWeight: 600,
                  cursor: 'pointer',
                  fontSize: '0.98rem'
                }}
              >
                إلغاء
              </button>
              <button
                onClick={saveEditGroup}
                disabled={!editGroupName.trim() || !editGroupMatiere || editGroupStudents.size === 0 || editGroupLoading}
                style={{
                  background: (editGroupName.trim() && editGroupMatiere && editGroupStudents.size > 0 && !editGroupLoading) ? '#fbbf24' : '#9ca3af',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '0.4rem',
                  padding: '0.4rem 1.1rem',
                  fontWeight: 600,
                  cursor: (editGroupName.trim() && editGroupMatiere && editGroupStudents.size > 0 && !editGroupLoading) ? 'pointer' : 'not-allowed',
                  fontSize: '0.98rem'
                }}
              >
                {editGroupLoading ? 'جاري الحفظ...' : `حفظ التعديلات (${editGroupStudents.size} طالب)`}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Group Confirmation Modal */}
      {showDeleteGroupConfirm && (
        <div style={modalOverlayStyle}>
          <div
            className="adminalert-modal"
            style={modalContentSmallStyle}
          >
            <div style={{
              background: '#fff',
              borderRadius: '0.7rem',
              padding: '1rem',
              width: '100%',
              maxWidth: '350px',
              textAlign: 'center',
              margin: 'auto'
            }}>
              <h2 style={{ color: '#dc2626', fontWeight: 700, marginBottom: '1rem' }}>تأكيد حذف المجموعة</h2>
              <p style={{ marginBottom: '1rem' }}>هل أنت متأكد أنك تريد حذف هذه المجموعة؟ لا يمكن التراجع عن هذا الإجراء.</p>
              <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'center' }}>
                <button
                  onClick={() => setShowDeleteGroupConfirm(false)}
                  style={{
                    background: '#6b7280',
                    color: '#fff',
                    border: 'none',
                    borderRadius: '0.4rem',
                    padding: '0.4rem 1.1rem',
                    fontWeight: 600,
                    cursor: 'pointer',
                    fontSize: '0.98rem'
                  }}
                >
                  إلغاء
                </button>
                <button
                  onClick={handleDeleteGroup}
                  style={{
                    background: '#dc2626',
                    color: '#fff',
                    border: 'none',
                    borderRadius: '0.4rem',
                    padding: '0.4rem 1.1rem',
                    fontWeight: 600,
                    cursor: 'pointer',
                    fontSize: '0.98rem'
                  }}
                >
                  حذف المجموعة
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Remove Student from Group Confirmation Modal */}
      {showRemoveStudentConfirm && (
        <div style={modalOverlayStyle}>
          <div
            className="adminalert-modal"
            style={modalContentSmallStyle}
          >
            <div style={{
              background: '#fff',
              borderRadius: '0.7rem',
              padding: '1rem',
              width: '100%',
              maxWidth: '350px',
              textAlign: 'center',
              margin: 'auto'
            }}>
              <h2 style={{ color: '#dc2626', fontWeight: 700, marginBottom: '1rem' }}>تأكيد حذف الطالب</h2>
              <p style={{ marginBottom: '1rem' }}>هل أنت متأكد أنك تريد حذف هذا الطالب من المجموعة؟</p>
              <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'center' }}>
                <button
                  onClick={() => setShowRemoveStudentConfirm(false)}
                  style={{
                    background: '#6b7280',
                    color: '#fff',
                    border: 'none',
                    borderRadius: '0.4rem',
                    padding: '0.4rem 1.1rem',
                    fontWeight: 600,
                    cursor: 'pointer',
                    fontSize: '0.98rem'
                  }}
                >
                  إلغاء
                </button>
                <button
                  onClick={handleRemoveStudentFromGroup}
                  style={{
                    background: '#dc2626',
                    color: '#fff',
                    border: 'none',
                    borderRadius: '0.4rem',
                    padding: '0.4rem 1.1rem',
                    fontWeight: 600,
                    cursor: 'pointer',
                    fontSize: '0.98rem'
                  }}
                >
                  حذف الطالب
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}